import { useEffect } from 'react';

interface FallbackColors {
  primary: string;
  primaryDark: string;
  slate: string;
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function darken(r: number, g: number, b: number, factor: number): string {
  return toHex(Math.round(r * factor), Math.round(g * factor), Math.round(b * factor));
}

/**
 * Publica as cores da marca como CSS variables.
 * Tenta extrair a cor de destaque e o cinza institucional diretamente da logo;
 * em qualquer falha (canvas bloqueado, imagem ausente) mantém a paleta padrão.
 */
export function ThemeFromLogo({ logoUrl, fallback }: { logoUrl: string; fallback: FallbackColors }): null {
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', fallback.primary);
    root.style.setProperty('--brand-primary-dark', fallback.primaryDark);
    root.style.setProperty('--brand-slate', fallback.slate);

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = logoUrl;

    image.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const width = Math.min(image.naturalWidth, 240);
        const scale = width / image.naturalWidth;
        canvas.width = width;
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);

        let accent = { r: 0, g: 0, b: 0, score: 0 };
        let neutral = { r: 0, g: 0, b: 0, count: 0 };

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i] ?? 0;
          const g = data[i + 1] ?? 0;
          const b = data[i + 2] ?? 0;
          const alpha = data[i + 3] ?? 0;
          if (alpha < 200) continue;
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          if (saturation > 0.45 && max > 90) {
            const score = saturation * max;
            if (score > accent.score) accent = { r, g, b, score };
          } else if (max < 160 && saturation < 0.25) {
            neutral = { r: neutral.r + r, g: neutral.g + g, b: neutral.b + b, count: neutral.count + 1 };
          }
        }

        if (accent.score > 0) {
          root.style.setProperty('--brand-primary', toHex(accent.r, accent.g, accent.b));
          root.style.setProperty('--brand-primary-dark', darken(accent.r, accent.g, accent.b, 0.62));
        }
        if (neutral.count > 0) {
          root.style.setProperty(
            '--brand-slate',
            toHex(
              Math.round(neutral.r / neutral.count),
              Math.round(neutral.g / neutral.count),
              Math.round(neutral.b / neutral.count),
            ),
          );
        }
      } catch {
        /* mantém fallback */
      }
    };

    return () => {
      cancelled = true;
    };
  }, [logoUrl, fallback.primary, fallback.primaryDark, fallback.slate]);

  return null;
}
